import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "./translations";

import type {
  SprengnetterValuationHubValuationPackageModelResponseAvmPriceResponse as PackageAvmPriceResponse,
  ValuationHubCoreModelRealEstateAvmEquipmentPriceRange,
} from "@/api/generatedApi";
import { PropertyTargetIcon } from "@/assets";
import { PanelHeader } from "@/components/panelHeader";
import { getAvmBaseRange, getAvmSelectedRange } from "@/helpers/avm";
import { getProsaWebBuildingStandardLabel } from "@/helpers/prosaWeb";
import { useModuleContext } from "@/hooks/useModuleContext";
import { useAppSelector } from "@/store/hooks";
import { colors } from "@/theme/tokens/colors";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

import { EquipmentStandardsGrid } from "./EquipmentStandardsGrid";
import { PanelActions } from "./PanelActions";
import { PriceRangeSlider } from "./PriceRangeSlider";
import { PropertyStandardInfluenceInfo } from "./PropertyStandardInfluenceInfo";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  panelContainer: {
    display: "grid",
    backgroundColor: "white",
    padding: theme.analysisLayout.panelPadding,
    gap: theme.analysisLayout.panelGap,
  },
  headerSection: {
    display: "grid",
    gap: theme.analysisLayout.panelGap,
  },
});

interface PricePositioningPanelProps {
  isLoading?: boolean;
  onSavePriceRange?: (range: [number, number]) => Promise<void> | void;
  isSaving?: boolean;
}

const SIMPLE_EQUIPMENT = "EINFACH";
const VERY_HIGH_EQUIPMENT = "STARK_GEHOBEN";
const DEFAULT_SLIDER_MIN = 50000;
const DEFAULT_SLIDER_MAX = 1000000;

const getFiniteValues = (...values: Array<number | null | undefined>): number[] =>
  values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));

const getMinValue = (fallback: number, ...values: Array<number | null | undefined>): number => {
  const finiteValues = getFiniteValues(...values);
  return finiteValues.length ? Math.min(...finiteValues) : fallback;
};

const getMaxValue = (fallback: number, ...values: Array<number | null | undefined>): number => {
  const finiteValues = getFiniteValues(...values);
  return finiteValues.length ? Math.max(...finiteValues) : fallback;
};

const getEquipmentRangeValue = (
  ranges: ValuationHubCoreModelRealEstateAvmEquipmentPriceRange[] | null | undefined,
  equipment: string,
): ValuationHubCoreModelRealEstateAvmEquipmentPriceRange | undefined =>
  ranges?.find((range) => range.equipment === equipment);

const hasEquipmentPriceRange = (priceRange: unknown): priceRange is PackageAvmPriceResponse =>
  !!priceRange && typeof priceRange === "object" && "equipment_price_range" in priceRange;

const formatCurrency = (value: number): string =>
  value.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

export const PricePositioningPanel = ({
  isLoading = false,
  onSavePriceRange,
  isSaving = false,
}: PricePositioningPanelProps): JSX.Element => {
  const { t } = useTranslation("PricePositioningPanel");
  const { t: tRealEstate } = useTranslation("Common", { keyPrefix: "realEstate" });
  const theme = useTheme();
  const styles = createStyles(theme);
  const { isValuationReport } = useModuleContext();
  const { propertyInformation, avmPriceRange, prosaWeb, reportEquipmentPriceRange, reportSelectedEquipmentPriceRange } =
    useAppSelector((state) =>
      isValuationReport
        ? {
            propertyInformation: state.valuationReport.propertyInformation,
            avmPriceRange: state.valuationReport.avm,
            prosaWeb: state.valuationReport.prosaWeb,
            reportEquipmentPriceRange: state.valuationReport.equipmentPriceRange,
            reportSelectedEquipmentPriceRange: state.valuationReport.selectedEquipmentPriceRange,
          }
        : {
            propertyInformation: state.currentValuation.propertyInformation,
            avmPriceRange: state.currentValuation.avm,
            prosaWeb: state.currentValuation.prosaWeb,
            reportEquipmentPriceRange: null,
            reportSelectedEquipmentPriceRange: null,
          },
    );

  const isPlot = propertyInformation?.generalInfo.objectType === ObjectTypeEnum.plotOfLand;

  const equipmentStandards = [
    { key: "simple", label: t("equipmentStandard.low"), equipment: SIMPLE_EQUIPMENT },
    { key: "medium", label: t("equipmentStandard.medium"), equipment: "MITTEL" },
    { key: "high", label: t("equipmentStandard.high"), equipment: "GEHOBEN" },
    { key: "veryHigh", label: t("equipmentStandard.veryHigh"), equipment: VERY_HIGH_EQUIPMENT },
  ] as const;

  const equipmentPriceRange = isPlot
    ? undefined
    : isValuationReport
      ? reportEquipmentPriceRange ?? undefined
      : hasEquipmentPriceRange(avmPriceRange)
        ? avmPriceRange.equipment_price_range
        : undefined;
  const avmValue = isValuationReport
    ? reportSelectedEquipmentPriceRange?.value
    : hasEquipmentPriceRange(avmPriceRange)
      ? avmPriceRange.value
      : undefined;
  const buildingStandardLabel = getProsaWebBuildingStandardLabel(
    prosaWeb?.factors?.building_standard?.value,
    tRealEstate,
  );
  const capitalizedBuildingStandardLabel =
    buildingStandardLabel.charAt(0).toLocaleUpperCase() + buildingStandardLabel.slice(1);
  const avmBaseRange = getAvmBaseRange(avmPriceRange);
  const avmSelectedRange = isValuationReport
    ? {
        min: reportSelectedEquipmentPriceRange?.min ?? null,
        max: reportSelectedEquipmentPriceRange?.max ?? null,
      }
    : getAvmSelectedRange(avmPriceRange);
  const simpleEquipmentRange = getEquipmentRangeValue(equipmentPriceRange, SIMPLE_EQUIPMENT);
  const veryHighEquipmentRange = getEquipmentRangeValue(equipmentPriceRange, VERY_HIGH_EQUIPMENT);
  const sliderMin = getMinValue(DEFAULT_SLIDER_MIN, simpleEquipmentRange?.min, avmBaseRange.min, avmSelectedRange.min);
  const sliderMax = getMaxValue(
    DEFAULT_SLIDER_MAX,
    veryHighEquipmentRange?.max,
    avmBaseRange.max,
    avmSelectedRange.max,
  );
  const avmSliderRange: [number, number] = [avmBaseRange.min ?? sliderMin, avmBaseRange.max ?? sliderMax];

  const initialUserSliderRangeFromApi = useMemo<[number, number]>(
    () => [
      avmSelectedRange.min ?? avmBaseRange.min ?? sliderMin,
      avmSelectedRange.max ?? avmBaseRange.max ?? sliderMax,
    ],
    [avmBaseRange.max, avmBaseRange.min, avmSelectedRange.max, avmSelectedRange.min, sliderMax, sliderMin],
  );

  const [initialUserSliderRange, setInitialUserSliderRange] = useState<[number, number]>(initialUserSliderRangeFromApi);
  const [userSliderRange, setUserSliderRange] = useState<[number, number]>(initialUserSliderRangeFromApi);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isLoading || !avmPriceRange || isEditing) return;

    setInitialUserSliderRange(initialUserSliderRangeFromApi);
    setUserSliderRange(initialUserSliderRangeFromApi);
  }, [avmPriceRange, initialUserSliderRangeFromApi, isEditing, isLoading]);

  const handleSliderChange = (_event: Event, value: number | number[]): void => {
    if (!Array.isArray(value)) return;
    setUserSliderRange([value[0], value[1]]);
    setIsEditing(true);
  };

  const showSelectedRange = isEditing || avmSelectedRange.min !== null || avmSelectedRange.max !== null;

  const handleApply = async (): Promise<void> => {
    if (onSavePriceRange) {
      await onSavePriceRange(userSliderRange);
    }

    setInitialUserSliderRange(userSliderRange);
    setIsEditing(false);
  };

  return (
    <Box sx={styles.panelContainer} data-cy="price-positioning-panel">
      <Box sx={styles.headerSection}>
        <PanelHeader
          title={t("title")}
          icon={
            <PropertyTargetIcon sx={{ fontSize: theme.analysisLayout.headerIconSize, color: colors.brand.brownDark }} />
          }
        />
        <Typography>{t(isPlot ? "description.plotText" : "description.text")}</Typography>
        {!isPlot && (
          <>
            <Box>
              {isLoading ? (
                <Skeleton animation="wave" variant="rounded" width={220} height={20} />
              ) : (
                <>
                  {t("determinedEquipmentStandard")}:{" "}
                  <Typography component="span" sx={{ color: "primary.main", fontWeight: 600 }}>
                    {capitalizedBuildingStandardLabel}
                  </Typography>
                </>
              )}
            </Box>

            <EquipmentStandardsGrid
              standards={equipmentStandards}
              ranges={equipmentPriceRange}
              livingArea={propertyInformation?.buildingData?.livingArea}
              avmValue={avmValue}
              isLoading={isLoading}
            />
          </>
        )}
        <Stack gap={theme.analysisLayout.compactGap}>
          <Typography>{t("title")}</Typography>
          <Typography variant="modalHeader">
            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={180} height={32} />
            ) : (
              `${formatCurrency(avmSliderRange[0])} - ${formatCurrency(avmSliderRange[1])}`
            )}
          </Typography>
          <Typography>{t("recommendedPriceRange")}</Typography>
        </Stack>

        <Stack gap={theme.analysisLayout.panelGap}>
          <PriceRangeSlider
            min={sliderMin}
            max={sliderMax}
            referenceValue={avmSliderRange}
            initialValue={initialUserSliderRange}
            value={userSliderRange}
            isEditing={isEditing}
            showBottomLabels={showSelectedRange}
            onChange={handleSliderChange}
            isLoading={isLoading}
          />
          {!isPlot && <PropertyStandardInfluenceInfo />}
          {isEditing ? (
            <PanelActions
              onStopEditing={() => {
                setUserSliderRange(initialUserSliderRange);
                setIsEditing(false);
              }}
              onApply={handleApply}
              isApplying={isSaving}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};
